import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["enableSnap", "scrollOffset", "slideGap", "hasFocusableContent"];
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import CustomPropTypes from '../../util/custom-prop-types';
import { StyledCarouselScrollContainer } from './CarouselScrollContainer.styles';

/**
 * The scrollable container for `Carousel` slides. Used by `Carousel`.
 *
 * Note that `role="list"` is explicitly set to force VoiceOver to announce this
 * as a list [source](https://www.tempertemper.net/blog/accessibility-issues-when-removing-list-markers).
 *
 * @since 8.71.0
 */
export var CarouselScrollContainer = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var enableSnap = _ref.enableSnap,
    scrollOffset = _ref.scrollOffset,
    slideGap = _ref.slideGap,
    hasFocusableContent = _ref.hasFocusableContent,
    rest = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/React.createElement(StyledCarouselScrollContainer, _extends({
    as: "ul",
    display: "flex",
    enableSnap: enableSnap,
    gap: slideGap,
    scrollOffset: scrollOffset,
    ref: forwardedRef,
    role: "list",
    tabIndex: hasFocusableContent ? undefined : 0
  }, rest));
});
CarouselScrollContainer.displayName = 'CarouselScrollContainer';
CarouselScrollContainer.propTypes = {
  /**
   * When true, the carousel will "snap" to the nearest slide after the user
   * clicks a nav control or finishes scrolling or swiping.
   *
   * By default, snap is enabled on desktop but disabled on mobile to protect
   * touch users from clunky, unexpected behavior. If needed, this can be changed
   * using an inline media query object.
   *
   * See the `slideAlign` and `scrollOffset` props for more controls.
   */
  enableSnap: CustomPropTypes.inlineMediaQuery(PropTypes.bool),
  /**
   * When false, the Carousel's content area will be focusable so that
   * users can control the container's scroll position with their keyboard
   */
  hasFocusableContent: PropTypes.bool,
  /**
   * Sets padding at the beginning and end of the slide container. Useful for preventing
   * box shadows from getting cropped.
   *
   * Takes a spacing t-shirt size or number multiplier. Defaults to 'xs'.
   *
   * Supports inline media query objects.
   */
  scrollOffset: CustomPropTypes.inlineMediaQuery(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  /**
   * Sets the gutter size between slides.
   *
   * Takes a spacing t-shirt size or number multiplier. Defaults to 'xs'.
   *
   * Supports inline media query objects.
   */
  slideGap: CustomPropTypes.inlineMediaQuery(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
};
CarouselScrollContainer.defaultProps = {
  enableSnap: {
    default: false,
    lg_gte: true
  },
  hasFocusableContent: true,
  scrollOffset: 'xs',
  slideGap: 'sm'
};